/**
 * OB Measurement Panel
 * User-friendly interface for fetal biometry measurements and calculations
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Divider,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  IconButton,
  Collapse,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import StraightenIcon from '@mui/icons-material/Straighten';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

import obCalc, {
  calculateEFW,
  calculateCompositeGA,
  formatGA,
  formatWeight,
  calculateEFWPercentile,
  eddFromGA,
  calculateHC_AC_Ratio,
  calculateFL_AC_Ratio,
  interpretAFI,
  interpretSDP,
} from '../../utils/obCalculations';

const MeasurementField = ({ label, value, onChange, unit = 'mm', tooltip, min = 0, max = 500 }) => (
  <TextField
    fullWidth
    size="small"
    label={label}
    type="number"
    value={value || ''}
    onChange={(e) => onChange(e.target.value ? parseFloat(e.target.value) : null)}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end" sx={{ minWidth: 'auto' }}>
          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', mr: 0.5 }}>
            {unit}
          </Typography>
          {tooltip && (
            <Tooltip title={tooltip} arrow placement="top">
              <IconButton size="small" sx={{ p: 0.25 }}>
                <InfoOutlinedIcon sx={{ fontSize: '0.875rem' }} />
              </IconButton>
            </Tooltip>
          )}
        </InputAdornment>
      ),
      inputProps: { min, max, step: 0.1 }
    }}
    sx={{
      '& .MuiInputBase-input': { textAlign: 'right', pr: 0.5 },
      '& .MuiInputBase-root': { pr: 0.5 },
    }}
  />
);

const ResultCard = ({ title, value, subtitle, color = 'primary', icon }) => (
  <Card
    elevation={0}
    sx={{
      bgcolor: color === 'primary' ? 'primary.light' : color === 'success' ? '#e8f5e9' : '#fff3e0',
      border: '1px solid',
      borderColor: color === 'primary' ? 'primary.main' : color === 'success' ? '#4caf50' : '#ff9800',
    }}
  >
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        {icon}
        <Typography variant="caption" color="textSecondary">
          {title}
        </Typography>
      </Box>
      <Typography variant="h5" fontWeight="bold" color={color === 'primary' ? 'primary.dark' : 'textPrimary'}>
        {value || '-'}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="textSecondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const OBMeasurementPanel = ({
  initialMeasurements = {},
  onMeasurementsChange,
  lmpDate,
  examDate = new Date(),
  readOnly = false
}) => {
  // Biometry measurements
  const [measurements, setMeasurements] = useState({
    crl: null,   // Crown-Rump Length
    bpd: null,   // Biparietal Diameter
    hc: null,    // Head Circumference
    ac: null,    // Abdominal Circumference
    fl: null,    // Femur Length
    ofd: null,   // Occipitofrontal Diameter
    hl: null,    // Humerus Length
    ...initialMeasurements,
  });

  // Amniotic fluid
  const [amnioticFluid, setAmnioticFluid] = useState({
    afi: null,   // Amniotic Fluid Index
    sdp: null,   // Single Deepest Pocket
  });

  // Calculated results
  const [results, setResults] = useState({
    efw: null,
    efwFormula: null,
    ga: null,
    gaMethod: null,
    gaIndividual: {},
    percentile: null,
    edd: null,
    hcAcRatio: null,
    flAcRatio: null,
  });

  // UI state
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Calculate results when measurements change
  const calculate = useCallback(() => {
    const { crl, bpd, hc, ac, fl, ofd } = measurements;

    // EFW
    const efwResult = calculateEFW({ bpd, hc, ac, fl });

    // GA
    const gaResult = calculateCompositeGA({ crl, bpd, hc, ac, fl });

    // Percentile
    let percentileResult = null;
    if (efwResult.value && gaResult?.weeks) {
      percentileResult = calculateEFWPercentile(efwResult.value, gaResult.weeks);
    }

    // EDD
    let edd = null;
    if (gaResult?.weeks) {
      edd = eddFromGA(gaResult.weeks, examDate);
    }

    // Ratios
    const hcAcRatio = calculateHC_AC_Ratio(hc, ac);
    const flAcRatio = calculateFL_AC_Ratio(fl, ac);

    setResults({
      efw: efwResult.value,
      efwFormula: efwResult.formula,
      ga: gaResult?.weeks,
      gaMethod: gaResult?.method,
      gaIndividual: gaResult?.individual || {},
      percentile: percentileResult,
      edd,
      hcAcRatio,
      flAcRatio,
    });
  }, [measurements, examDate]);

  useEffect(() => {
    calculate();
  }, [calculate]);

  useEffect(() => {
    if (onMeasurementsChange) {
      onMeasurementsChange({ measurements, amnioticFluid, results });
    }
  }, [measurements, amnioticFluid, results, onMeasurementsChange]);

  const updateMeasurement = (key, value) => {
    setMeasurements(prev => ({ ...prev, [key]: value }));
  };

  const updateAmnioticFluid = (key, value) => {
    setAmnioticFluid(prev => ({ ...prev, [key]: value }));
  };

  const afiInterpretation = interpretAFI(amnioticFluid.afi);
  const sdpInterpretation = interpretSDP(amnioticFluid.sdp);

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0' }}>
      {/* Results Summary - stacked vertically for narrow panel */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
        <ResultCard
          title="Estimated Fetal Weight"
          value={formatWeight(results.efw)}
          subtitle={results.efwFormula}
          color="primary"
          icon={<ChildCareIcon fontSize="small" color="primary" />}
        />
        <ResultCard
          title="Gestational Age"
          value={formatGA(results.ga)}
          subtitle={results.gaMethod}
          color="success"
          icon={<CalendarTodayIcon fontSize="small" sx={{ color: '#4caf50' }} />}
        />
        <ResultCard
          title="Growth Percentile"
          value={results.percentile ? `${results.percentile.percentile}%` : '-'}
          subtitle={results.percentile ? `Z-score: ${results.percentile.zScore}` : null}
          color={
            results.percentile?.percentile < 10 || results.percentile?.percentile > 90
              ? 'warning'
              : 'success'
          }
          icon={<StraightenIcon fontSize="small" sx={{ color: '#ff9800' }} />}
        />
      </Box>

      {/* EDD Display */}
      {results.edd && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Estimated Due Date:</strong> {results.edd.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Primary Biometry */}
      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
        Fetal Biometry
      </Typography>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <MeasurementField
            label="BPD"
            value={measurements.bpd}
            onChange={(v) => updateMeasurement('bpd', v)}
            tooltip="Biparietal Diameter - Outer to inner skull edge"
          />
        </Grid>
        <Grid item xs={6}>
          <MeasurementField
            label="HC"
            value={measurements.hc}
            onChange={(v) => updateMeasurement('hc', v)}
            tooltip="Head Circumference"
          />
        </Grid>
        <Grid item xs={6}>
          <MeasurementField
            label="AC"
            value={measurements.ac}
            onChange={(v) => updateMeasurement('ac', v)}
            tooltip="Abdominal Circumference"
          />
        </Grid>
        <Grid item xs={6}>
          <MeasurementField
            label="FL"
            value={measurements.fl}
            onChange={(v) => updateMeasurement('fl', v)}
            tooltip="Femur Length - Diaphysis only"
          />
        </Grid>
      </Grid>

      {/* Individual GA from each measurement */}
      {Object.keys(results.gaIndividual).length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>
            Individual GA:
          </Typography>
          {Object.entries(results.gaIndividual).map(([key, value]) => (
            <Chip
              key={key}
              label={`${key.toUpperCase()}: ${formatGA(value)}`}
              size="small"
              variant="outlined"
              sx={{ mr: 0.5, mb: 0.5 }}
            />
          ))}
        </Box>
      )}

      {/* Advanced Measurements Toggle */}
      <Box
        onClick={() => setShowAdvanced(!showAdvanced)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          py: 1,
          '&:hover': { bgcolor: 'action.hover' },
          borderRadius: 1,
        }}
      >
        {showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
          {showAdvanced ? 'Hide' : 'Show'} Additional Measurements
        </Typography>
      </Box>

      <Collapse in={showAdvanced}>
        <Grid container spacing={1.5} sx={{ mt: 1 }}>
          {/* First Trimester */}
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              First Trimester
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <MeasurementField
              label="CRL"
              value={measurements.crl}
              onChange={(v) => updateMeasurement('crl', v)}
              tooltip="Crown-Rump Length (7-14 weeks)"
            />
          </Grid>

          {/* Additional Biometry */}
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              Additional Biometry
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <MeasurementField
              label="OFD"
              value={measurements.ofd}
              onChange={(v) => updateMeasurement('ofd', v)}
              tooltip="Occipitofrontal Diameter"
            />
          </Grid>
          <Grid item xs={6}>
            <MeasurementField
              label="HL"
              value={measurements.hl}
              onChange={(v) => updateMeasurement('hl', v)}
              tooltip="Humerus Length"
            />
          </Grid>

          {/* Amniotic Fluid */}
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              Amniotic Fluid
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <MeasurementField
              label="AFI"
              value={amnioticFluid.afi}
              onChange={(v) => updateAmnioticFluid('afi', v)}
              unit="cm"
              tooltip="Amniotic Fluid Index (sum of 4 quadrants)"
            />
          </Grid>
          <Grid item xs={6}>
            <MeasurementField
              label="SDP"
              value={amnioticFluid.sdp}
              onChange={(v) => updateAmnioticFluid('sdp', v)}
              unit="cm"
              tooltip="Single Deepest Pocket"
            />
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" gap={0.5} alignItems="center" flexWrap="wrap">
              {afiInterpretation && (
                <Chip
                  label={`AFI: ${afiInterpretation.status}`}
                  size="small"
                  color={
                    afiInterpretation.severity === 'normal' ? 'success' :
                    afiInterpretation.severity === 'borderline' ? 'warning' : 'error'
                  }
                />
              )}
              {sdpInterpretation && (
                <Chip
                  label={`SDP: ${sdpInterpretation.status}`}
                  size="small"
                  color={
                    sdpInterpretation.severity === 'normal' ? 'success' :
                    sdpInterpretation.severity === 'borderline' ? 'warning' : 'error'
                  }
                />
              )}
            </Box>
          </Grid>

          {/* Ratios */}
          {(results.hcAcRatio || results.flAcRatio) && (
            <>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  Biometric Ratios
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" gap={0.5} flexWrap="wrap">
                  {results.hcAcRatio && (
                    <Chip
                      label={`HC/AC: ${results.hcAcRatio}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {results.flAcRatio && (
                    <Chip
                      label={`FL/AC: ${results.flAcRatio}`}
                      size="small"
                      variant="outlined"
                      color={results.flAcRatio < 0.20 || results.flAcRatio > 0.24 ? 'warning' : 'default'}
                    />
                  )}
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </Collapse>
    </Paper>
  );
};

export default OBMeasurementPanel;
